import React from 'react';
import ReactDOM from 'react-dom';
import useModal from '../../hooks/useModal';

/**
 * Modal de confirmación reutilizable para acciones destructivas o importantes.
 * Soporta variantes visuales y estado de carga.
 *
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Controla si el modal está visible
 * @param {string} props.title - Título mostrado en el header del modal
 * @param {string|React.ReactNode} props.message - Mensaje de confirmación (texto o JSX)
 * @param {string} [props.confirmText='Confirmar'] - Texto del botón de confirmación
 * @param {string} [props.cancelText='Cancelar'] - Texto del botón de cancelación
 * @param {Function} props.onConfirm - Callback ejecutado al confirmar
 * @param {Function} [props.onCancel] - Callback ejecutado al cancelar
 * @param {Function} [props.onClose] - Alias de onCancel para compatibilidad con otros modales
 * @param {string} [props.variant='danger'] - Variante visual: 'danger', 'warning', 'info', 'success'
 * @param {boolean} [props.loading=false] - Si true, deshabilita botones y muestra "Procesando..."
 * @returns {JSX.Element|null} Null si isOpen es false
 */
function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  onClose, // Alias para onCancel (por compatibilidad)
  variant = 'danger',
  loading = false
}) {
  // Usar onCancel si existe, sino usar onClose como fallback
  const handleCancel = onCancel || onClose;

  const { handleBackdropClick } = useModal(isOpen, handleCancel, { loading });

  if (!isOpen) return null;

  // Iconos por variante
  const icons = {
    danger: '⚠️',
    warning: '⚡',
    info: 'ℹ️',
    success: '✅'
  };

  // Clases CSS por variante
  const iconClasses = {
    danger: 'modal-icon-danger',
    warning: 'modal-icon-warning',
    info: 'modal-icon-info',
    success: 'modal-icon-success'
  };

  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className="modal-header">
          <div className="modal-header-content">
            <span className={iconClasses[variant] || iconClasses.danger}>
              {icons[variant] || icons.danger}
            </span>
            <h2 className="modal-title" id="confirm-dialog-title">{title}</h2>
          </div>
          <button
            className="modal-close-btn"
            onClick={handleCancel}
            disabled={loading}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={`btn ${variant === 'danger' ? 'btn-danger' : variant === 'warning' ? 'btn-warning' : variant === 'success' ? 'btn-success' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Procesando...' : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmDialog;
